import React, {
    FC,
    useCallback,
    useEffect,
    useMemo,
    useRef,
} from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import {
    partsSelectActions,
    apiGetActions,
    lamePageOpenDetectionAction,
} from '@app/react/action';
import { IinitState } from '@app/react/reducer';
import type { AppState } from '@app/react/reducer';
import { OrderCommon2SelectComponent } from '@component/OrderCommon2SelectComponent';
import { hideEcHeaderNavi } from '@root/app/react/common/Function';
import { useHistoryGoParts } from '@root/app/react/contexts/useHistoryGoParts';
import { OnSelectProductHandler } from '@app/react/uikit/ProductThumbnail';
import { useOrderPathParams } from '@app/react/contexts/useOrderPathParams';
import { GetResponseOrderParts } from '@app/react/const/type';
import { useMediaDesktop, useMediaMobile } from '@app/react/common/useMedia';
import { useWindowScrollPosition } from '@app/react/hooks/useWindowScrollPosition';
import { noProduct } from '@app/react/const/products/noProduct';

const selectProduct = (state: AppState): GetResponseOrderParts | null => state.APIData.lame;

const LameHelp: FC<{ isDesktop: boolean }> = ({ isDesktop }) => (
    <>
        <p className={classNames('section-header', { hidden: isDesktop })}>
            ラメ加工
        </p>
        <p className="order-typezero-select__description">
            下地の糸色によって見え方や発色が異なります。
            明るめのはっきりとした色のものの上にのせるととてもきれいに仕上がります。
            「プチデコレーション」との組み合わせもお薦めです。
        </p>
    </>
);

export const OrderLameSelectContainer: FC<{ waitFetch?: boolean }> = ({ waitFetch = false }) => {
    const dispatch = useDispatch();
    const goParts = useHistoryGoParts();
    const { arrowType, orderType } = useOrderPathParams();
    const isDesktop = useMediaDesktop();
    const isMobile = useMediaMobile();
    const scrollObj = useRef(null);
    useWindowScrollPosition('scroll_lame', true, scrollObj);

    const customizeData = useSelector<AppState, IinitState>(
        (state) => state.customize,
    );
    const lameProductList = useSelector(selectProduct);
    const partsData = useMemo(() => {
        const base = lameProductList?.partsData ?? [];
        return [noProduct, ...base];
    }, [lameProductList]);

    const onSelectProduct: OnSelectProductHandler = useCallback(
        (__, product) => {
            dispatch(partsSelectActions.lame.started({ param: product }));
        },
        [dispatch],
    );

    const handleOk = () => {
        goParts();
    };

    useEffect(() => {
        hideEcHeaderNavi(isDesktop, isMobile, dispatch);
    }, [isDesktop, isMobile, dispatch]);

    useEffect(() => {
        if (waitFetch) {
            console.log('initializing, waiting fetch');
            return;
        }
        batch(() => {
            dispatch(
                apiGetActions.lame.started({
                    arrowType,
                    orderType,
                    selectItems: customizeData,
                }),
            );
            dispatch(lamePageOpenDetectionAction({ open: true }));
        });

        return () => {
            dispatch(lamePageOpenDetectionAction({ open: false }));
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, arrowType, orderType, waitFetch]);

    // add LameHtml
    return (
        <OrderCommon2SelectComponent
            rootRef={scrollObj}
            classes="order-lame-select"
            partsNameProps="ラメ加工"
            customizeData={customizeData}
            onSelectProduct={onSelectProduct}
            partsData={partsData}
            onOk={handleOk}
            kind="lame"
            LameHtml={<LameHelp isDesktop={isDesktop} />}
        />
    );
};
